import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { InventoryEntities } from 'client/data/models/inventory';
import { ADVERTISED_PRICE_SOURCE } from 'site-modules/shared/constants/inventory/prices';
import { DeliveryTooltip } from 'site-modules/shared/components/delivery-tooltip/delivery-tooltip';

export function PriceLabel({
  price,
  prices,
  displayMSRP = false,
  displayLeaseMonthlyPayment = false,
  displayLoanMonthlyPayment = false,
  deliveryFee = undefined,
  includeDeliveryFee = false,
  dealerFeesLink = null,
}) {
  const advertisedPrice = get(prices, 'advertisedPrice', 0);
  const advertisedPriceSource = get(prices, 'advertisedPriceSource');
  const isInternetPrice = advertisedPriceSource === ADVERTISED_PRICE_SOURCE.INTERNET_PRICE;

  if (displayMSRP) {
    const isDealerMsrpPrice = advertisedPriceSource === ADVERTISED_PRICE_SOURCE.DEALER_MSRP;
    const msrpPrice = !isDealerMsrpPrice ? get(prices, 'dealerMsrp', 0) : 0;
    const msrpStrikethrough = get(prices, 'dealerMsrpStrikethroughIsCompliant', false) && msrpPrice !== advertisedPrice;

    return (
      <>
        <span
          className={classNames('heading-3', {
            'text-decoration-line-through': isDealerMsrpPrice && msrpStrikethrough,
          })}
        >
          {getPriceStringV2(price)}
        </span>
        {dealerFeesLink}
        <div
          className={classNames('text-gray-darker fw-normal size-14', {
            'text-decoration-line-through': !!msrpPrice && msrpStrikethrough,
          })}
        >
          MSRP{!!msrpPrice && ` ${getPriceStringV2(msrpPrice)}`}
        </div>
      </>
    );
  }

  const downPayment = get(prices, 'estimateLeasePromise.downPayment', 0);
  const formattedSummaryPrice =
    includeDeliveryFee && deliveryFee ? getPriceStringV2(price + deliveryFee) : getPriceStringV2(price);

  return (
    <>
      <span className="heading-3">{formattedSummaryPrice}</span>
      {includeDeliveryFee &&
        (deliveryFee >= 0 ? (
          <DeliveryTooltip price={price} deliveryFee={deliveryFee} className="ms-0_25" classNameWrapper="p-1" />
        ) : (
          <div className="small text-underline mb-0_5">Delivery Costs Unknown</div>
        ))}
      {displayLeaseMonthlyPayment && (
        <>
          <span className="text-nowrap size-12">/mo est.</span>
          <div className="size-12">{getPriceStringV2(downPayment)} down</div>
        </>
      )}
      {displayLoanMonthlyPayment && (
        <>
          <span className="text-nowrap size-12">/mo est.</span>
          <div className="size-12">
            {getPriceStringV2(advertisedPrice)} {isInternetPrice ? 'Price' : 'MSRP'}
          </div>
        </>
      )}
      {dealerFeesLink}
    </>
  );
}

PriceLabel.propTypes = {
  price: PropTypes.number.isRequired,
  prices: InventoryEntities.Prices.isRequired,
  displayMSRP: PropTypes.bool,
  displayLeaseMonthlyPayment: PropTypes.bool,
  displayLoanMonthlyPayment: PropTypes.bool,
  deliveryFee: PropTypes.number,
  includeDeliveryFee: PropTypes.bool,
  dealerFeesLink: PropTypes.node,
};
